import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { ImageManager } from "@/pages/admin/ImageManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium">{label}</Label>
    {children}
    {hint && <p className="text-xs text-neutral-500">{hint}</p>}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm" data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function AdminPageEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/pages/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errText(e)));
  }, [id]);

  const set = (k, v) => {
    setP((o) => ({ ...o, [k]: v }));
    setDirty(true);
  };

  const save = async (override) => {
    const src = override || p;
    setSaving(true);
    try {
      const body = {
        slug: src.slug, title: src.title, menu_label: src.menu_label, description: src.description,
        columns: Number(src.columns), columns_mobile: Number(src.columns_mobile), aspect_ratio: src.aspect_ratio,
        in_menu: src.in_menu, hidden: src.hidden, seo_title: src.seo_title, seo_description: src.seo_description,
        images: (src.images || []).map(({ media_id, alt, width, height, animated }) => ({ media_id, alt, width, height, animated })),
        password: pw ? pw : src.remove_password ? "" : null,
      };
      const { data } = await adminApi.put(`/admin/pages/${id}`, body);
      setP(data);
      setPw("");
      setDirty(false);
      toast.success("Saved — live on the site now");
    } catch (ex) {
      toast.error(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the page "${p.title}"? This cannot be undone.`)) return;
    await adminApi.delete(`/admin/pages/${id}`);
    toast.success("Page deleted");
    nav("/admin");
  };

  if (!p) return <AdminLayout title="Loading…" back="/admin" />;
  const link = `${window.location.origin}/${p.slug}`;

  return (
    <AdminLayout
      title={p.title}
      back="/admin"
      actions={
        <>
          <Button variant="outline" className="rounded-full" onClick={() => window.open(`/${p.slug}`, "_blank")} data-testid="view-page-button"><ExternalLink size={15} className="mr-1" /> View</Button>
          <Button className="rounded-full min-w-[110px]" disabled={saving} onClick={() => save()} data-testid="save-page-button">{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</Button>
        </>
      }
    >
      <section className="bg-white rounded-2xl p-5 sm:p-6 mb-6">
        <h2 className="font-semibold mb-4">Photos ({(p.images || []).length})</h2>
        <ImageManager
          images={p.images}
          uploadPath={`/admin/pages/${id}/images`}
          onChange={(imgs, uploaded) => {
            if (uploaded) {
              setP((o) => ({ ...o, images: imgs }));
            } else set("images", imgs);
          }}
        />
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl p-5 sm:p-6 space-y-4">
          <h2 className="font-semibold">Page</h2>
          <Field label="Title"><Input value={p.title} onChange={(e) => set("title", e.target.value)} data-testid="page-title-input" /></Field>
          <Field label="Address" hint={link}>
            <Input value={p.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="page-slug-input" />
          </Field>
          <Field label="Menu label" hint="Press Enter for a line break, like on the current menu.">
            <Textarea rows={2} value={p.menu_label} onChange={(e) => set("menu_label", e.target.value)} data-testid="page-menu-label-input" />
          </Field>
          <Field label="Info text (the green “i” panel)">
            <Textarea rows={5} value={p.description} onChange={(e) => set("description", e.target.value)} data-testid="page-description-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Columns">
              <Select value={String(p.columns)} onChange={(v) => set("columns", v)} options={[["1", "1"], ["2", "2"], ["3", "3"], ["4", "4"]]} testid="page-columns-select" />
            </Field>
            <Field label="Phone cols">
              <Select value={String(p.columns_mobile)} onChange={(v) => set("columns_mobile", v)} options={[["1", "1"], ["2", "2"]]} testid="page-columns-mobile-select" />
            </Field>
            <Field label="Shape">
              <Select value={p.aspect_ratio} onChange={(v) => set("aspect_ratio", v)} options={[["1:1", "Square"], ["4:3", "4:3"], ["3:2", "3:2"], ["16:9", "16:9"], ["3:4", "Portrait 3:4"], ["2:3", "Portrait 2:3"]]} testid="page-aspect-select" />
            </Field>
          </div>
        </section>

        <div className="space-y-6">
          <section className="bg-white rounded-2xl p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold">Visibility</h2>
            <div className="flex items-center justify-between gap-4">
              <div><div className="text-sm font-medium">Hidden page</div><div className="text-xs text-neutral-500">Not in the menu or Google. Only people with the link.</div></div>
              <Switch checked={p.hidden} onCheckedChange={(v) => set("hidden", v)} data-testid="page-hidden-switch" />
            </div>
            {!p.hidden && (
              <div className="flex items-center justify-between gap-4">
                <div><div className="text-sm font-medium">Show in menu</div></div>
                <Switch checked={p.in_menu} onCheckedChange={(v) => set("in_menu", v)} data-testid="page-in-menu-switch" />
              </div>
            )}
            <Field label={p.has_password ? "Password (set) — type a new one to change it" : "Password"} hint="Visitors must enter this to see the page. Leave empty for link-only.">
              <Input type="text" value={pw} onChange={(e) => { setPw(e.target.value); setDirty(true); }} placeholder={p.has_password ? "••••••••" : "No password"} data-testid="page-password-input" />
            </Field>
            {p.has_password && (
              <button type="button" className="text-xs underline text-neutral-600" onClick={() => save({ ...p, remove_password: true })} data-testid="remove-password-button">Remove password</button>
            )}
            <div className="flex items-center gap-2">
              <Input readOnly value={link} className="text-xs" data-testid="page-share-link" />
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="copy-page-link-button"><Copy size={15} /></Button>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold">Google &amp; AI search</h2>
            <Field label="Search title" hint={`${(p.seo_title || "").length}/60 characters`}>
              <Input value={p.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder={`${p.title} | Joe Tanis Photography`} data-testid="page-seo-title-input" />
            </Field>
            <Field label="Search description" hint={`${(p.seo_description || "").length}/160 characters`}>
              <Textarea rows={3} value={p.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="page-seo-description-input" />
            </Field>
            <Field label="About this work (long text)" hint={`${(p.seo_body || "").trim().split(/\s+/).filter(Boolean).length} words — first line is the heading; blank line between paragraphs. Shown further down inside the green “i” panel.`}>
              <Textarea rows={10} value={p.seo_body || ""} onChange={(e) => set("seo_body", e.target.value)} data-testid="page-seo-body-input" />
            </Field>
          </section>

          <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove} data-testid="delete-page-button"><Trash2 size={15} className="mr-1" /> Delete page</Button>
        </div>
      </div>
    </AdminLayout>
  );
}
